import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ThemeContext } from "../../lib/theme";
import type { Theme } from "../../lib/tokens";

const STORAGE_KEY = "assawave-theme";
const DEFAULT_THEME: Theme = "dark"; // 결정 #4: site 기본 다크 + 라이트 토글

function readInitial(): Theme {
  if (typeof document === "undefined") return DEFAULT_THEME;
  // 1. <html>에 이미 설정된 data-theme (index.html bootstrap)
  const attr = document.documentElement.dataset.theme;
  if (attr === "dark" || attr === "light") return attr;
  // 2. 저장된 사용자 토글
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
  } catch {
    // localStorage unavailable
  }
  // 3. 사이트 기본
  return DEFAULT_THEME;
}

type Props = {
  children: ReactNode;
  /** 강제 테마 (예: dark 전용 라우트 — portal/admin) */
  force?: Theme;
};

export default function ThemeProvider({ children, force }: Props) {
  const [theme, setTheme] = useState<Theme>(() => force ?? readInitial());

  useEffect(() => {
    if (force) {
      document.documentElement.dataset.theme = force;
      setTheme(force);
      return;
    }
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // localStorage unavailable — ignore.
    }
  }, [theme, force]);

  const toggle = useCallback(() => {
    if (force) return;
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, [force]);

  const set = useCallback(
    (t: Theme) => {
      if (force) return;
      setTheme(t);
    },
    [force],
  );

  const value = useMemo(
    () => ({ theme, isDark: theme === "dark", toggle, set }),
    [theme, toggle, set],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
